import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Briefcase, Users, CheckCircle, Award, Quote } from 'lucide-react';

const stats = [
  { id: 1, value: "150+", label: "Successful Projects", icon: <Briefcase size={28} /> },
  { id: 2, value: "80+", label: "Happy Clients", icon: <Users size={28} /> },
  { id: 3, value: "99%", label: "Client Retention", icon: <CheckCircle size={28} /> },
  { id: 4, value: "10+", label: "Years Experience", icon: <Award size={28} /> },
];

const testimonials = [
  {
    id: 1,
    name: "Michael Roberts",
    role: "CTO, FinTech Global",
    text: "Businex Technologies transformed our legacy systems into a modern cloud architecture flawlessly. Their engineers are not just coders; they are strategic problem solvers who delivered perfectly on time.",
    rating: 5
  },
  {
    id: 2,
    name: "Sarah Jenkins",
    role: "Director of Product, RetailChain",
    text: "Working with the Businex team was an incredible experience. From the initial architecture phase to the final deployment of our mobile app, they demonstrated exceptional skill and extreme attention to detail.",
    rating: 5
  },
  {
    id: 3,
    name: "David Chen",
    role: "Founder, HealthMetrics",
    text: "They built our patient data platform with incredible precision and absolute adherence to compliance frameworks. I highly recommend Businex Technologies for any complex enterprise software needs.",
    rating: 5
  },
  {
    id: 4,
    name: "Elena Rodriguez",
    role: "CEO, InnovateX",
    text: "The level of professionalism and the UI/UX design quality they brought to our SaaS product was unmatched. Businex is officially our long-term digital partner.",
    rating: 5
  }
];

const TestimonialsAndStats = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  // Helper to calculate dynamic card positioning for coverflow style
  const getCardStyles = (index) => {
    const diff = (index - currentIndex + testimonials.length) % testimonials.length;
    
    // Active Card
    if (diff === 0) {
      return { 
        x: 0, 
        y: 0,
        scale: 1, 
        zIndex: 30, 
        opacity: 1,
        rotateY: 0
      };
    }
    // Next Card (Right)
    if (diff === 1) {
      return { 
        x: "50%", 
        y: 20,
        scale: 0.85, 
        zIndex: 20, 
        opacity: 0.6,
        rotateY: -10
      };
    }
    // Previous Card (Left)
    if (diff === testimonials.length - 1) {
      return { 
        x: "-50%", 
        y: 20,
        scale: 0.85, 
        zIndex: 20, 
        opacity: 0.6,
        rotateY: 10
      };
    }
    // Hidden Cards
    return { 
      x: 0, 
      y: 40,
      scale: 0.7, 
      zIndex: 10, 
      opacity: 0,
      rotateY: 0
    };
  };

  return (
    <section className="py-24 bg-transparent relative overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        
        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-32">
          {stats.map((stat, i) => (
            <motion.div 
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-3xl p-8 flex flex-col items-center justify-center text-center shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
            >
              <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                {stat.icon}
              </div>
              <h3 className="text-4xl font-extrabold text-slate-900 mb-2">{stat.value}</h3>
              <p className="text-slate-600 font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Testimonials Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
           <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 font-semibold text-sm mb-6">
             <Star size={16} fill="currentColor" /> Client Experiences
           </div>
           <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
             Loved by our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Partners</span>
           </h2>
           <p className="text-lg text-slate-600 leading-relaxed">
             Hear directly from those who have accelerated their digital transformation with our expert development teams.
           </p>
        </div>

        {/* Improved Interactive Carousel Wrapper */}
        <div className="relative h-[400px] flex items-center justify-center [perspective:1000px]">
          {testimonials.map((testimonial, idx) => {
             const styles = getCardStyles(idx);
             return (
               <motion.div
                 key={testimonial.id}
                 animate={styles}
                 transition={{ duration: 0.6, type: "spring", bounce: 0.2 }}
                 className="absolute w-full max-w-2xl bg-white rounded-3xl p-8 md:p-12 shadow-[0_20px_50px_rgb(0,0,0,0.06)] border border-slate-100 flex flex-col"
                 style={{ transformOrigin: "center center" }}
                 onClick={() => setCurrentIndex(idx)}
               >
                 <Quote size={80} className="text-blue-50 absolute right-8 top-8 rotate-180 z-0" />
                 
                 {/* Card Header */}
                 <div className="flex items-center gap-5 relative z-10 mb-8">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-md ring-4 ring-blue-50">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg">{testimonial.name}</h4>
                      <p className="text-blue-600 text-sm font-semibold">{testimonial.role}</p>
                    </div>
                 </div>

                 {/* Card Body */}
                 <div className="relative z-10">
                    <div className="flex gap-1 text-amber-400 mb-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} size={18} fill="currentColor" />
                      ))}
                    </div>
                    <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                 </div>
               </motion.div>
             );
          })}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-8 mt-12">
          <button 
            onClick={prevTestimonial}
            className="w-12 h-12 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-600 hover:text-white hover:bg-slate-900 transition-all hover:scale-105"
          >
            <ChevronLeft size={24} />
          </button>
          
          <div className="flex gap-3">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2.5 rounded-full transition-all duration-500 ease-out ${
                  currentIndex === idx ? 'bg-slate-900 w-10' : 'bg-slate-200 w-2.5 hover:bg-slate-300'
                }`}
                aria-label={`Select testimonial ${idx + 1}`}
              />
            ))}
          </div>

          <button 
            onClick={nextTestimonial}
            className="w-12 h-12 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-600 hover:text-white hover:bg-slate-900 transition-all hover:scale-105"
          >
            <ChevronRight size={24} />
          </button>
        </div>

      </div>
    </section>
  );
};

export default TestimonialsAndStats;
